import { useState, useCallback, useRef, useEffect } from 'react';
import '../../stat/css/onboarding_form.css';
import agreyaLogo from '../../assets/images/logo.png';
import kristalLogo from '../../assets/images/kristal_onboarding.png';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// Formats a Date as yyyy-mm-dd using local time
const toISODate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// Text input for yyyy/mm/dd dates, with a basic calendar popup behind the 📅 button
function DateField({ id, name, value, hasError, onChange, min, max }) {
    const wrapperRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(new Date());

    const selectedISO = /^\d{4}\/\d{2}\/\d{2}$/.test(value || '') ? value.replace(/\//g, '-') : '';
    const todayISO = toISODate(new Date());

    // Close on outside click or Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleMouseDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    const toggleCalendar = () => {
        if (!isOpen) {
            // Start on the selected date, otherwise on the latest allowed date (or today)
            const base = selectedISO ? new Date(`${selectedISO}T00:00:00`) : new Date(`${max || todayISO}T00:00:00`);
            setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
        }
        setIsOpen(!isOpen);
    };

    const pickDate = (iso) => {
        onChange({ target: { name, value: iso.replace(/-/g, '/') } });
        setIsOpen(false);
    };

    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    // Year dropdown covers the field's min/max, or 1900 to 20 years ahead by default
    const minYear = min ? Number(min.slice(0, 4)) : 1900;
    const maxYear = max ? Number(max.slice(0, 4)) : new Date().getFullYear() + 20;
    const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

    // Monday-first grid for the shown month
    const leadingBlanks = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [
        ...Array(leadingBlanks).fill(null),
        ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
    ];

    const isDisabled = (iso) => (min && iso < min) || (max && iso > max);

    return (
        <div className="date-field" ref={wrapperRef}>
            <input
                type="text"
                inputMode="numeric"
                id={id}
                name={name}
                className={`form-control ${hasError ? 'error' : ''}`}
                value={value || ''}
                onChange={onChange}
                placeholder="yyyy/mm/dd"
                maxLength={10}
            />
            <button type="button" className="date-field-button" aria-label="Open calendar" aria-expanded={isOpen} onClick={toggleCalendar}>
                📅
            </button>
            {isOpen && (
                <div className="date-picker" role="dialog" aria-label="Choose a date">
                    <div className="date-picker-header">
                        <button type="button" className="date-picker-nav" aria-label="Previous month" onClick={() => setViewMonth(new Date(year, month - 1, 1))}>‹</button>
                        <select className="date-picker-select" aria-label="Month" value={month} onChange={(e) => setViewMonth(new Date(year, Number(e.target.value), 1))}>
                            {MONTH_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}
                        </select>
                        <select className="date-picker-select" aria-label="Year" value={year} onChange={(e) => setViewMonth(new Date(Number(e.target.value), month, 1))}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <button type="button" className="date-picker-nav" aria-label="Next month" onClick={() => setViewMonth(new Date(year, month + 1, 1))}>›</button>
                    </div>
                    <div className="date-picker-weekdays">
                        {WEEKDAY_LABELS.map(label => <span key={label}>{label}</span>)}
                    </div>
                    <div className="date-picker-grid">
                        {cells.map((day, index) => {
                            if (day === null) return <span key={`blank-${index}`} />;
                            const iso = toISODate(new Date(year, month, day));
                            const classes = ['date-picker-day', iso === selectedISO ? 'selected' : '', iso === todayISO ? 'today' : ''].join(' ').trim();
                            return (
                                <button key={iso} type="button" className={classes} disabled={isDisabled(iso)} onClick={() => pickDate(iso)}>
                                    {day}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function KristalKYCForm() {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [alertMessage, setAlertMessage] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const formRef = useRef(null);

    const totalSteps = 10;

    // Employment types that need the employer / role / address details
    const EMPLOYED_TYPES = ['salaried', 'self_employed', 'business_owner'];
    const isEmploymentDetailRequired = (type) => EMPLOYED_TYPES.includes(type);

    const steps = [
        { title: 'Account Type', icon: '👤', fields: ['account_type', 'investor_profile', 'ai_professional_criterion', 'ai_evidence_date'] },
        { title: 'Personal Details', icon: '📋', fields: ['first_name', 'last_name', 'date_of_birth', 'email', 'mobile', 'nationality', 'country_of_birth', 'place_of_birth', 'other_nationalities', 'residential_address', 'passport_number', 'passport_expiry', 'marital_status', 'dependents', 'aadhar_number'] },
        { title: 'Employment Details', icon: '💼', fields: ['employment_type', 'employer_name', 'employer_industry', 'role_occupation', 'designation', 'employment_address', 'income_activity'] },
        { title: 'Tax & Wealth Details', icon: '💰', fields: ['tax_jurisdiction_1', 'tax_jurisdiction_2', 'tax_jurisdiction_3', 'us_person', 'crs_certification', 'total_wealth', 'declared_net_worth', 'net_worth_corroborated', 'current_annual_income', 'estimated_annual_income'] },
        { title: 'Source of Investments', icon: '📊', fields: ['salary_pension', 'inheritance', 'investment_income', 'allowance_spouse', 'other_sources'] },
        { title: 'Investment & Funding', icon: '💰', fields: ['anticipated_investment', 'funding_method', 'remitting_bank'] },
        { title: 'Wealth Narrative', icon: '📝', fields: ['wealth_narrative', 'key_corroborating_documents'] },
        { title: 'Profiling', icon: '🎯', fields: ['education_level', 'portfolio_loss', 'financial_experience', 'comfortable_downside', 'pep_status', 'accounts_held', 'transaction_0', 'transaction_1', 'transaction_2', 'transaction_3', 'transaction_4'] },
        { title: 'PEP Appendix', icon: '📄', fields: ['pep_position_government', 'pep_political_party', 'pep_regulatory_body', 'pep_legal_matters', 'pep_aum_kristal'] },
        { title: 'Documents Register', icon: '📋', fields: ['doc_id_proof_status', 'doc_residency_status', 'doc_tax_id_status', 'doc_ai_evidence_status', 'doc_source_funds_status', 'doc_source_wealth_status', 'doc_other_status'] },
    ];

    const showAlert = useCallback((message, type = 'info') => {
        setAlertMessage({ message, type });
        setTimeout(() => setAlertMessage(null), 5000);
    }, []);

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const validatePhone = (phone) => {
        const regex = /^[\d+\-\s()]+$/;
        if (!regex.test(phone)) return false;
        const digits = phone.replace(/\D/g, '');
        return digits.length >= 10;
    };

    const validateCurrency = (value) => {
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(String(value).trim());
    };

    // Dates are typed as yyyy/mm/dd; slashes are inserted automatically
    const formatYMDInput = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 4) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 4)}/${digits.slice(4)}`;
        return `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6)}`;
    };

    // Returns a Date for a real calendar date in yyyy/mm/dd, otherwise null
    const parseYMD = (value) => {
        const match = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(String(value || '').trim());
        if (!match) return null;
        const [y, m, d] = match.slice(1).map(Number);
        const date = new Date(y, m - 1, d);
        if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
        return date;
    };

    const todayAtMidnight = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    // Statement date must be today or earlier, and no older than 90 days from the application date
    const isWithinLast90Days = (date) => {
        const today = todayAtMidnight();
        const cutoff = new Date(today);
        cutoff.setDate(cutoff.getDate() - 90);
        return date <= today && date >= cutoff;
    };

    const isAiEvidenceRequired = formData.investor_profile === 'accredited';
    const isEmploymentRequired = isEmploymentDetailRequired(formData.employment_type);
    const isUSPerson = formData.us_person === 'yes';

    // Calendar bounds for the AI evidence date: today back to 90 days ago
    const evidenceMinDate = todayAtMidnight();
    evidenceMinDate.setDate(evidenceMinDate.getDate() - 90);

    // Drop a leftover AI evidence error once the investor is no longer Accredited
    useEffect(() => {
        if (!isAiEvidenceRequired) {
            setErrors(prev => (prev.ai_evidence_date ? { ...prev, ai_evidence_date: '' } : prev));
        }
    }, [isAiEvidenceRequired]);

    const canNavigateToStep = useCallback((targetStep) => {
        // Check if all previous steps are filled
        for (let i = 0; i < targetStep; i++) {
            if (i === 0) {
                if (!formData.account_type || !formData.investor_profile || !formData.ai_professional_criterion) return false;
                if (isAiEvidenceRequired && !formData.ai_evidence_date) return false;
            }
            if (i === 1) {
                if (!formData.first_name || !formData.last_name || !formData.date_of_birth || !formData.email || !formData.mobile || !formData.nationality || !formData.country_of_birth || !formData.place_of_birth || !formData.residential_address || !formData.passport_number || !formData.passport_expiry) return false;
            }
            if (i === 2) {
                if (!formData.employment_type) return false;
                if (isEmploymentRequired && (!formData.employer_name || !formData.role_occupation || !formData.designation || !formData.employment_address)) return false;
            }
            if (i === 3) {
                if (isUSPerson) return false;
                if (!formData.tax_jurisdiction_1 || !formData.us_person || !formData.crs_certification || !formData.total_wealth || !formData.declared_net_worth || !formData.current_annual_income || !formData.estimated_annual_income) return false;
            }
            if (i === 5) {
                if (!formData.anticipated_investment || !formData.funding_method || !formData.remitting_bank) return false;
            }
            if (i === 6) {
                if (!formData.wealth_narrative) return false;
            }
            if (i === 7) {
                if (!formData.education_level || !formData.portfolio_loss || !formData.financial_experience || !formData.comfortable_downside || !formData.pep_status) return false;
            }
            if (i === 8 && formData.pep_status === 'Yes') {
                if (!formData.pep_position_government || !formData.pep_political_party || !formData.pep_regulatory_body || !formData.pep_legal_matters) return false;
            }
        }
        return true;
    }, [formData, steps, isAiEvidenceRequired, isEmploymentRequired, isUSPerson]);

    const handleStepClick = useCallback((targetStep) => {
        if (targetStep === currentStep) return; // Already on this step
        if (targetStep < currentStep) {
            // Allow going back anytime
            setCurrentStep(targetStep);
            return;
        }
        // Going forward: check if all previous steps are filled
        if (canNavigateToStep(targetStep)) {
            setCurrentStep(targetStep);
        } else {
            showAlert('Please fill all required fields in previous steps', 'error');
        }
    }, [currentStep, canNavigateToStep, showAlert]);

    const validateStep = useCallback((stepIndex) => {
        const newErrors = {};
        const step = steps[stepIndex];

        if (stepIndex === 0) {
            if (!formData.account_type) newErrors.account_type = 'Account type is required';
            if (!formData.investor_profile) newErrors.investor_profile = 'Investor profile is required';
            if (!formData.ai_professional_criterion) newErrors.ai_professional_criterion = 'AI/Professional criterion is required';
            if (isAiEvidenceRequired) {
                const evidenceDate = parseYMD(formData.ai_evidence_date);
                if (!formData.ai_evidence_date) newErrors.ai_evidence_date = 'AI evidence statement date is required';
                else if (!evidenceDate) newErrors.ai_evidence_date = 'Enter a valid date in yyyy/mm/dd format';
                else if (!isWithinLast90Days(evidenceDate)) newErrors.ai_evidence_date = 'Statement date must be within the last 90 days';
            }
        }

        if (stepIndex === 1) {
            if (!formData.first_name) newErrors.first_name = 'First name is required';
            if (!formData.last_name) newErrors.last_name = 'Last name is required';
            if (!formData.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
            else if (!parseYMD(formData.date_of_birth)) newErrors.date_of_birth = 'Enter a valid date in yyyy/mm/dd format';
            else if (parseYMD(formData.date_of_birth) >= todayAtMidnight()) newErrors.date_of_birth = 'Date of birth must be in the past';
            if (!formData.email) newErrors.email = 'Email is required';
            else if (!validateEmail(formData.email)) newErrors.email = 'Invalid email format';
            if (!formData.mobile) newErrors.mobile = 'Mobile is required';
            else if (!validatePhone(formData.mobile)) newErrors.mobile = 'Invalid phone format';
            if (!formData.nationality) newErrors.nationality = 'Nationality is required';
            if (!formData.country_of_birth) newErrors.country_of_birth = 'Country of birth is required';
            if (!formData.place_of_birth) newErrors.place_of_birth = 'Place of birth is required';
            if (!formData.residential_address) newErrors.residential_address = 'Address is required';
            if (!formData.passport_number) newErrors.passport_number = 'Passport number is required';
            if (!formData.passport_expiry) newErrors.passport_expiry = 'Passport expiry date is required';
            else if (!parseYMD(formData.passport_expiry)) newErrors.passport_expiry = 'Enter a valid date in yyyy/mm/dd format';
            else if (parseYMD(formData.passport_expiry) < todayAtMidnight()) newErrors.passport_expiry = 'Passport has expired - expiry must be on or after the submission date';
            if (formData.aadhar_number && !/^\d{16}$/.test(formData.aadhar_number)) newErrors.aadhar_number = 'Aadhar number must be exactly 16 digits';
        }

        if (stepIndex === 2) {
            if (!formData.employment_type) newErrors.employment_type = 'Employment type is required';
            // Employer details are only mandatory for salaried, self-employed and business owners
            if (isEmploymentRequired) {
                if (!formData.employer_name) newErrors.employer_name = 'Employer name is required';
                if (!formData.role_occupation) newErrors.role_occupation = 'Role is required';
                if (!formData.designation) newErrors.designation = 'Designation is required';
                if (!formData.employment_address) newErrors.employment_address = 'Employment address is required';
            }
        }

        if (stepIndex === 3) {
            if (isUSPerson) newErrors.us_person = 'Kristal does not onboard US Persons. This application cannot proceed.';
            if (!formData.tax_jurisdiction_1) newErrors.tax_jurisdiction_1 = 'Tax jurisdiction is required';
            if (!formData.us_person) newErrors.us_person = 'US Person status is required';
            if (!formData.crs_certification) newErrors.crs_certification = 'CRS certification status is required';
            if (!formData.total_wealth) newErrors.total_wealth = 'Wealth band is required';
            if (!formData.declared_net_worth) newErrors.declared_net_worth = 'Declared net worth is required';
            else if (!validateCurrency(formData.declared_net_worth)) newErrors.declared_net_worth = 'Invalid currency';
            if (!formData.current_annual_income) newErrors.current_annual_income = 'Annual income band is required';
            // Accepts a number or text (e.g. "250,000 from salary")
            if (!formData.estimated_annual_income || !String(formData.estimated_annual_income).trim()) newErrors.estimated_annual_income = 'Estimated annual income is required';
        }

        if (stepIndex === 4) {
            // Source of Investments - no required fields, just numbers
        }

        if (stepIndex === 5) {
            if (!formData.anticipated_investment) newErrors.anticipated_investment = 'Anticipated investment is required';
            else if (!validateCurrency(formData.anticipated_investment)) newErrors.anticipated_investment = 'Invalid currency';
            if (!formData.funding_method) newErrors.funding_method = 'Funding method is required';
        }

        if (stepIndex === 6) {
            // Wealth Narrative - no required fields
        }

        if (stepIndex === 7) {
            if (!formData.education_level) newErrors.education_level = 'Education level is required';
            if (!formData.portfolio_loss) newErrors.portfolio_loss = 'Portfolio loss is required';
            if (!formData.financial_experience) newErrors.financial_experience = 'Experience is required';
            if (!formData.comfortable_downside) newErrors.comfortable_downside = 'Risk tolerance is required';
            if (!formData.pep_status) newErrors.pep_status = 'PEP status is required';
        }

        if (stepIndex === 8) {
            // PEP Appendix - only required if PEP = Yes
            if (formData.pep_status === 'yes') {
                if (!formData.pep_position_government) newErrors.pep_position_government = 'This field is required';
                if (!formData.pep_political_party) newErrors.pep_political_party = 'This field is required';
                if (!formData.pep_regulatory_body) newErrors.pep_regulatory_body = 'This field is required';
                if (!formData.pep_legal_matters) newErrors.pep_legal_matters = 'This field is required';
                if (!formData.pep_aum_kristal) newErrors.pep_aum_kristal = 'This field is required';
            }
        }

        if (stepIndex === 9) {
            // Documents Register - no required fields, just tracking status
        }

        if (stepIndex === 10) {
            if (!formData.id_proof) newErrors.id_proof = 'ID Proof is required';
            if (!formData.address_proof) newErrors.address_proof = 'Residency Proof is required';
            if (!formData.tax_id_copy) newErrors.tax_id_copy = 'Tax ID Copy is required';
            if (!formData.declaration) newErrors.declaration = 'You must accept the declaration';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, steps, isAiEvidenceRequired, isEmploymentRequired, isUSPerson, isWithinLast90Days]);

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    }, [errors]);

    const handleDateChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: formatYMDInput(value) }));
        setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
    }, []);

    const handleDigitsChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 16) }));
        setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
    }, []);

    const handleMultiSelect = useCallback((name, value) => {
        setFormData(prev => {
            const current = prev[name] || [];
            const isArray = Array.isArray(current);
            const array = isArray ? current : [current].filter(x => x);

            if (array.includes(value)) {
                return { ...prev, [name]: array.filter(x => x !== value) };
            } else {
                return { ...prev, [name]: [...array, value] };
            }
        });
    }, []);

    const handleFileChange = useCallback((e, fieldName) => {
        const { name, files } = e.target;
        const field = fieldName || name;
        if (files && files[0]) {
            setFormData(prev => ({
                ...prev,
                [field]: files[0]
            }));
        }
    }, []);

    const handleNext = () => {
        if (currentStep === 3 && isUSPerson) {
            showAlert('Kristal does not onboard US Persons. This application cannot proceed.', 'error');
            return;
        }
        if (validateStep(currentStep)) {
            if (currentStep < totalSteps - 1) {
                setCurrentStep(currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            showAlert('Please fill all required fields', 'error');
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isUSPerson) {
            showAlert('Kristal does not onboard US Persons. This application cannot proceed.', 'error');
            return;
        }

        if (!validateStep(currentStep)) {
            showAlert('Please fill all required fields', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();

            Object.keys(formData).forEach(key => {
                if (formData[key] instanceof File) {
                    formDataToSend.append(key, formData[key]);
                } else if (Array.isArray(formData[key])) {
                    formData[key].forEach(item => {
                        formDataToSend.append(key, item);
                    });
                } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
                    formDataToSend.append(key, formData[key]);
                }
            });

            const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/kristal/api/onboarding/`;
            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formDataToSend,
                credentials: 'include'
            });

            const result = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                setFormData({});
                setErrors({});
                setCurrentStep(0);
            } else {
                showAlert(result.message || 'An error occurred', 'error');
                if (result.errors) {
                    setErrors(result.errors);
                }
            }
        } catch (error) {
            console.error('Submission error:', error);
            showAlert('Failed to submit form. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        if (window.confirm('Are you sure you want to reset the form?')) {
            setFormData({});
            setErrors({});
            setCurrentStep(0);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (event) => {
        event.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = useCallback((e, fieldName) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            setFormData(prev => ({
                ...prev,
                [fieldName]: files[0]
            }));
        }
    }, []);

    const currentStepData = steps[currentStep];

    if (isSubmitted) {
        return (
            <div className="kristal-container">
                <div className="form-section" style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: '48px', color: 'var(--primary-color)' }}>✓</div>
                    <h2 className="section-title">Form submitted successfully</h2>
                    <p>Thank you for choosing Agreya. Our team will review your application and get back to you if we need anything further.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="kristal-container">
            <div className="kristal-page-header">
                {/* <div className="kristal-header-top">
                    <div className="kristal-logo-section">
                        <div className="kristal-logo">⚜ KRISTAL</div>
                    </div>
                    <div className="kristal-header-title">CLIENT KYC - CLIENT PROFILER</div>
                </div> */}
                <div className="kristal-brand-logos">
                    <img src={agreyaLogo} alt="Agreya Capital" className="kristal-brand-logo kristal-brand-logo-agreya" />
                    <span className="kristal-brand-divider" aria-hidden="true"></span>
                    <img src={kristalLogo} alt="Kristal" className="kristal-brand-logo kristal-brand-logo-kristal" />
                </div>
                <div className="kristal-header-content">
                    {/* <div className="kristal-category-label">CLIENT KYC</div> */}
                    <h1 className="kristal-page-title">Client Profiler</h1>
                    <p className="kristal-page-note">We will use information from this form to prefill client profiler on Kristal portal. Further, once Kristal confirms document sufficiency, a link will be raised to the client. The client then completes further steps in the app - the registered unique email and mobile number are used for OTPs throughout.</p>
                </div>
                {/* <div className="kristal-step-indicator">
                    Step {currentStep + 1} of {totalSteps}: {currentStepData.title}
                </div> */}
            </div>

            {alertMessage && (
                <div className={`alert alert-${alertMessage.type}`} style={{ marginBottom: '30px' }}>
                    <span className="alert-icon">
                        {alertMessage.type === 'success' ? '✓' :
                         alertMessage.type === 'error' ? '✕' : 'ⓘ'}
                    </span>
                    <span>{alertMessage.message}</span>
                </div>
            )}

            <div className="form-progress">
                {steps.map((step, index) => {
                    const isClickable = index <= currentStep || canNavigateToStep(index);
                    return (
                        <div
                            key={index}
                            className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''} ${isClickable ? 'clickable' : 'locked'}`}
                            title={step.title}
                            onClick={() => isClickable && handleStepClick(index)}
                            role="button"
                            tabIndex={isClickable ? 0 : -1}
                            onKeyPress={(e) => isClickable && (e.key === 'Enter' || e.key === ' ') && handleStepClick(index)}
                        >
                            <span className="progress-step-name">{step.title}</span>
                        </div>
                    );
                })}
            </div>

            <form ref={formRef} style={{ marginBottom: '40px' }}>
                <div style={{ minHeight: '400px' }}>
                    {currentStep === 0 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div className="form-group">
                                <label htmlFor="accountType">Account Type <span className="label-required">*</span></label>
                                <p className="help-text">For a Joint account, please complete the Joint application.</p>
                                <select
                                    id="accountType"
                                    name="account_type"
                                    className={`form-control ${errors.account_type ? 'error' : ''}`}
                                    value={formData.account_type || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select account type</option>
                                    <option value="single">Single</option>
                                    <option value="joint_primary">Joint (Primary)</option>
                                    <option value="joint_secondary">Joint (Secondary)</option>
                                </select>
                                {errors.account_type && <span className="error-message">{errors.account_type}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="investorProfile">Investor Profile <span className="label-required">*</span></label>
                                <p className="help-text">Accredited Investor (Private Wealth): total net worth &gt; USD 1,000,000 or equivalent.</p>
                                <select
                                    id="investorProfile"
                                    name="investor_profile"
                                    className={`form-control ${errors.investor_profile ? 'error' : ''}`}
                                    value={formData.investor_profile || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select investor profile</option>
                                    <option value="retail">Retail Investor</option>
                                    <option value="accredited">Accredited Investor (Private Wealth)</option>
                                </select>
                                {errors.investor_profile && <span className="error-message">{errors.investor_profile}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="aiCriterion">AI / Professional Client Criterion Met <span className="label-required">*</span></label>
                                <p className="help-text">MAS total net-worth test may include property (primary residence capped at SGD 1M). Provident fund (EPF/PPF) counts; insurance policies do not.</p>
                                <select
                                    id="aiCriterion"
                                    name="ai_professional_criterion"
                                    className={`form-control ${errors.ai_professional_criterion ? 'error' : ''}`}
                                    value={formData.ai_professional_criterion || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select criterion</option>
                                    <option value="mas_personal_2m">MAS: Net personal assets &gt; SGD 2M (residence capped at SGD 1M)</option>
                                    <option value="mas_financial_1m">MAS: Net financial assets &gt; SGD 1M</option>
                                    <option value="income_300k">Income &gt;= SGD 300K (last 12 months)</option>
                                    <option value="fsra_1m">FSRA: Net assets &gt;= USD 1M excl. primary residence</option>
                                </select>
                                {errors.ai_professional_criterion && <span className="error-message">{errors.ai_professional_criterion}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="aiEvidence">
                                    AI Evidence - Statement Date(s) {isAiEvidenceRequired && <span className="label-required">*</span>}
                                </label>
                                <p className="help-text">Required only for Accredited Investors. Format yyyy/mm/dd - e.g. 2026/07/31 (latest month-end). Must be within the last 90 days, in the client's own name, not password-protected.</p>
                                <DateField
                                    id="aiEvidence"
                                    name="ai_evidence_date"
                                    value={formData.ai_evidence_date}
                                    hasError={isAiEvidenceRequired && errors.ai_evidence_date}
                                    onChange={handleDateChange}
                                    min={toISODate(evidenceMinDate)}
                                    max={toISODate(todayAtMidnight())}
                                />
                                {isAiEvidenceRequired && errors.ai_evidence_date && <span className="error-message">{errors.ai_evidence_date}</span>}
                            </div>
                        </div>
                    )}

                    {currentStep === 1 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">First Name <span className="label-required">*</span></label>
                                    <p className="help-text">As per passport.</p>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="first_name"
                                        className={`form-control ${errors.first_name ? 'error' : ''}`}
                                        value={formData.first_name || ''}
                                        onChange={handleInputChange}
                                    />
                                    {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="lastName">Last Name <span className="label-required">*</span></label>
                                    <p className="help-text">As per passport.</p>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="last_name"
                                        className={`form-control ${errors.last_name ? 'error' : ''}`}
                                        value={formData.last_name || ''}
                                        onChange={handleInputChange}
                                    />
                                    {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="dateOfBirth">Date of Birth <span className="label-required">*</span></label>
                                <p className="help-text">Format yyyy/mm/dd - e.g. 1975/04/23.</p>
                                <DateField
                                    id="dateOfBirth"
                                    name="date_of_birth"
                                    value={formData.date_of_birth}
                                    hasError={errors.date_of_birth}
                                    onChange={handleDateChange}
                                    max={toISODate(todayAtMidnight())}
                                />
                                {errors.date_of_birth && <span className="error-message">{errors.date_of_birth}</span>}
                            </div>

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label htmlFor="email">Email Address (unique to this client) <span className="label-required">*</span></label>
                                    <p className="help-text">OTPs and account notifications go here.</p>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className={`form-control ${errors.email ? 'error' : ''}`}
                                        value={formData.email || ''}
                                        onChange={handleInputChange}
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="mobile">Mobile Number (unique, with country code) <span className="label-required">*</span></label>
                                    <p className="help-text">e.g. +971 50 123 4567.</p>
                                    <input
                                        type="tel"
                                        id="mobile"
                                        name="mobile"
                                        className={`form-control ${errors.mobile ? 'error' : ''}`}
                                        value={formData.mobile || ''}
                                        onChange={handleInputChange}
                                    />
                                    {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="nationality">Nationality <span className="label-required">*</span></label>
                                <input
                                    type="text"
                                    id="nationality"
                                    name="nationality"
                                    className={`form-control ${errors.nationality ? 'error' : ''}`}
                                    value={formData.nationality || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.nationality && <span className="error-message">{errors.nationality}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="otherNationalities">Other / Previous Nationalities & Second Passports</label>
                                <p className="help-text">Include country and passport / ID number, state 'None' if not applicable.</p>
                                <textarea
                                    id="otherNationalities"
                                    name="other_nationalities"
                                    className="form-control"
                                    value={formData.other_nationalities || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="countryOfBirth">Country of Birth <span className="label-required">*</span></label>
                                <input
                                    type="text"
                                    id="countryOfBirth"
                                    name="country_of_birth"
                                    className={`form-control ${errors.country_of_birth ? 'error' : ''}`}
                                    value={formData.country_of_birth || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.country_of_birth && <span className="error-message">{errors.country_of_birth}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="placeOfBirth">Place of Birth (city / town) <span className="label-required">*</span></label>
                                <p className="help-text">Passports typically evidence the country only.</p>
                                <input
                                    type="text"
                                    id="placeOfBirth"
                                    name="place_of_birth"
                                    className={`form-control ${errors.place_of_birth ? 'error' : ''}`}
                                    value={formData.place_of_birth || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.place_of_birth && <span className="error-message">{errors.place_of_birth}</span>}
                            </div>

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label htmlFor="maritalStatus">Marital Status</label>
                                    <select
                                        id="maritalStatus"
                                        name="marital_status"
                                        className="form-control"
                                        value={formData.marital_status || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">SELECT...</option>
                                        <option value="single">Single</option>
                                        <option value="married">Married</option>
                                        <option value="divorced">Divorced</option>
                                        <option value="widowed">Widowed</option>
                                        <option value="separated">Separated</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="dependents">Number of Dependents</label>
                                    <input
                                        type="number"
                                        id="dependents"
                                        name="dependents"
                                        className="form-control"
                                        value={formData.dependents || ''}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="address">Current Residential Address <span className="label-required">*</span></label>
                                <p className="help-text">Complete address with city, state, postal code / P.O. Box.</p>
                                <textarea
                                    id="address"
                                    name="residential_address"
                                    className={`form-control ${errors.residential_address ? 'error' : ''}`}
                                    value={formData.residential_address || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.residential_address && <span className="error-message">{errors.residential_address}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="passport">Passport Number + Expiry Date <span className="label-required">*</span></label>
                                <p className="help-text">e.g. Z1234567 - expiry yyyy/mm/dd, e.g. 2031/05/14.</p>
                                <div className="form-group-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            id="passport"
                                            name="passport_number"
                                            className={`form-control ${errors.passport_number ? 'error' : ''}`}
                                            value={formData.passport_number || ''}
                                            onChange={handleInputChange}
                                        />
                                        {errors.passport_number && <span className="error-message">{errors.passport_number}</span>}
                                    </div>
                                    <div className="form-group">
                                        <DateField
                                            id="passportExpiry"
                                            name="passport_expiry"
                                            value={formData.passport_expiry}
                                            hasError={errors.passport_expiry}
                                            onChange={handleDateChange}
                                            min={toISODate(todayAtMidnight())}
                                        />
                                        {errors.passport_expiry && <span className="error-message">{errors.passport_expiry}</span>}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="aadhar">AADHAR Number</label>
                                <p className="help-text">If resident of India. 16 digits, numbers only.</p>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    id="aadhar"
                                    name="aadhar_number"
                                    className={`form-control ${errors.aadhar_number ? 'error' : ''}`}
                                    value={formData.aadhar_number || ''}
                                    onChange={handleDigitsChange}
                                    placeholder="16-digit Aadhar number"
                                    maxLength={16}
                                />
                                {errors.aadhar_number && <span className="error-message">{errors.aadhar_number}</span>}
                            </div>
                        </div>
                    )}

                    {currentStep === 2 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div className="form-group">
                                <label htmlFor="employment_type">Employment Type <span className="label-required">*</span></label>
                                <select
                                    id="employment_type"
                                    name="employment_type"
                                    className={`form-control ${errors.employment_type ? 'error' : ''}`}
                                    value={formData.employment_type || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">SELECT...</option>
                                    <option value="salaried">Salaried</option>
                                    <option value="self_employed">Self-Employed</option>
                                    <option value="business_owner">Business Owner</option>
                                    <option value="private_investor">Private Investor</option>
                                    <option value="retired">Retired</option>
                                    <option value="not_employed">Not Employed</option>
                                    <option value="homemaker">Homemaker</option>
                                    <option value="student">Student</option>
                                </select>
                                {errors.employment_type && <span className="error-message">{errors.employment_type}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="employer_name">Employer Name {isEmploymentRequired && <span className="label-required">*</span>}</label>
                                <input
                                    type="text"
                                    id="employer_name"
                                    name="employer_name"
                                    className={`form-control ${errors.employer_name ? 'error' : ''}`}
                                    value={formData.employer_name || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.employer_name && <span className="error-message">{errors.employer_name}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="employer_industry">Employer Industry</label>
                                <input
                                    type="text"
                                    id="employer_industry"
                                    name="employer_industry"
                                    className="form-control"
                                    value={formData.employer_industry || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="role_occupation">Role / Occupation {isEmploymentRequired && <span className="label-required">*</span>}</label>
                                <input
                                    type="text"
                                    id="role_occupation"
                                    name="role_occupation"
                                    className={`form-control ${errors.role_occupation ? 'error' : ''}`}
                                    value={formData.role_occupation || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.role_occupation && <span className="error-message">{errors.role_occupation}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="designation">Designation / Title {isEmploymentRequired && <span className="label-required">*</span>}</label>
                                <input
                                    type="text"
                                    id="designation"
                                    name="designation"
                                    className={`form-control ${errors.designation ? 'error' : ''}`}
                                    value={formData.designation || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.designation && <span className="error-message">{errors.designation}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="employment_address">Employment Address {isEmploymentRequired && <span className="label-required">*</span>}</label>
                                <p className="help-text">Complete address with city, state, postal code / P.O. Box.</p>
                                <textarea
                                    id="employment_address"
                                    name="employment_address"
                                    className={`form-control ${errors.employment_address ? 'error' : ''}`}
                                    value={formData.employment_address || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.employment_address && <span className="error-message">{errors.employment_address}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="income_activity">If not employed - nature of current activity</label>
                                <p className="help-text">e.g. private investor / retired - since when, and how ongoing income arises.</p>
                                <textarea
                                    id="income_activity"
                                    name="income_activity"
                                    className="form-control"
                                    value={formData.income_activity || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                        </div>
                    )}

                    {currentStep === 3 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div className="form-group">
                                <label htmlFor="tax_jurisdiction_1">Tax Jurisdiction 1 + Tax ID <span className="label-required">*</span></label>
                                <p className="help-text">e.g. INDIA + ABCDE1234F. A copy of the official tax ID must be on record.</p>
                                <input
                                    type="text"
                                    id="tax_jurisdiction_1"
                                    name="tax_jurisdiction_1"
                                    className={`form-control ${errors.tax_jurisdiction_1 ? 'error' : ''}`}
                                    value={formData.tax_jurisdiction_1 || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.tax_jurisdiction_1 && <span className="error-message">{errors.tax_jurisdiction_1}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="tax_jurisdiction_2">Tax Jurisdiction 2 + Tax ID</label>
                                <p className="help-text">If applicable.</p>
                                <input
                                    type="text"
                                    id="tax_jurisdiction_2"
                                    name="tax_jurisdiction_2"
                                    className="form-control"
                                    value={formData.tax_jurisdiction_2 || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="tax_jurisdiction_3">Tax Jurisdiction 3 + Tax ID</label>
                                <p className="help-text">If applicable.</p>
                                <input
                                    type="text"
                                    id="tax_jurisdiction_3"
                                    name="tax_jurisdiction_3"
                                    className="form-control"
                                    value={formData.tax_jurisdiction_3 || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-alert" style={{ backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px', padding: '12px', marginBottom: '20px', fontSize: '13px', color: '#1565c0' }}>
                                <strong>Kristal does NOT onboard US Persons. If the answer is Yes, the account cannot be opened - stop and inform the respective RM.</strong>
                            </div>

                            {isUSPerson && (
                                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                                    <strong>US Person selected - this application is stopped.</strong> Kristal cannot onboard US Persons, so the form cannot be filled further. Please inform the respective RM.
                                </div>
                            )}

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label htmlFor="us_person">US Person? <span className="label-required">*</span></label>
                                    <p className="help-text">Kristal does NOT onboard US Persons. A 'Yes' answer means the client cannot be onboarded.</p>
                                    <select
                                        id="us_person"
                                        name="us_person"
                                        className={`form-control ${errors.us_person ? 'error' : ''}`}
                                        value={formData.us_person || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">SELECT...</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                    {errors.us_person && <span className="error-message">{errors.us_person}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="crs_certification">CRS self-certification completed & attached? <span className="label-required">*</span></label>
                                    <select
                                        id="crs_certification"
                                        name="crs_certification"
                                        className={`form-control ${errors.crs_certification ? 'error' : ''}`}
                                        value={formData.crs_certification || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">SELECT...</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                    {errors.crs_certification && <span className="error-message">{errors.crs_certification}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="total_wealth">Current total wealth (band) <span className="label-required">*</span></label>
                                <select
                                    id="total_wealth"
                                    name="total_wealth"
                                    className={`form-control ${errors.total_wealth ? 'error' : ''}`}
                                    value={formData.total_wealth || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">SELECT...</option>
                                    <option value="usd_0_360k">USD 0 - 360,000</option>
                                    <option value="usd_360k_720k">USD 360,000 - 720,000</option>
                                    <option value="usd_720k_1430k">USD 720,000 - 1,430,000</option>
                                    <option value="usd_1430k_3580k">USD 1,430,000 - 3,580,000</option>
                                    <option value="usd_3580k_above">USD 3,580,000 &amp; Above</option>
                                </select>
                                {errors.total_wealth && <span className="error-message">{errors.total_wealth}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="declared_net_worth">Declared total net worth (USD - exact figure) <span className="label-required">*</span></label>
                                <p className="help-text">Corroboration of a minimum of 50% of the declared net worth is required.</p>
                                <input
                                    type="text"
                                    id="declared_net_worth"
                                    name="declared_net_worth"
                                    className={`form-control ${errors.declared_net_worth ? 'error' : ''}`}
                                    value={formData.declared_net_worth || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.declared_net_worth && <span className="error-message">{errors.declared_net_worth}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="net_worth_corroborated">Net worth corroborated (USD + documents relied upon)</label>
                                <p className="help-text">List each document, institution and statement date; must be &gt;= 50% of the declared figure.</p>
                                <textarea
                                    id="net_worth_corroborated"
                                    name="net_worth_corroborated"
                                    className="form-control"
                                    value={formData.net_worth_corroborated || ''}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="current_annual_income">Current annual net income (band) <span className="label-required">*</span></label>
                                <select
                                    id="current_annual_income"
                                    name="current_annual_income"
                                    className={`form-control ${errors.current_annual_income ? 'error' : ''}`}
                                    value={formData.current_annual_income || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">SELECT...</option>
                                    <option value="usd_0_80k">USD 0 - 80K</option>
                                    <option value="usd_80k_220k">USD 80K - 220K</option>
                                    <option value="usd_220k_360k">USD 220K - 360K</option>
                                    <option value="usd_360k_above">USD 360K &amp; Above</option>
                                </select>
                                {errors.current_annual_income && <span className="error-message">{errors.current_annual_income}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="estimated_annual_income">Estimated annual income (USD, exact) & primary income source <span className="label-required">*</span></label>
                                <input
                                    type="text"
                                    id="estimated_annual_income"
                                    name="estimated_annual_income"
                                    className={`form-control ${errors.estimated_annual_income ? 'error' : ''}`}
                                    value={formData.estimated_annual_income || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.estimated_annual_income && <span className="error-message">{errors.estimated_annual_income}</span>}
                            </div>
                        </div>
                    )}

                    {currentStep === 4 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px 12px', marginBottom: '20px', fontWeight: '700', fontSize: '13px' }}>
                                SOURCE OF INVESTMENTS (%) - MUST TOTAL 100
                            </div>

                            <div className="form-group">
                                <label htmlFor="salary_pension">Salary / pension income</label>
                                <p className="help-text">Whole number, e.g. 40.</p>
                                <input
                                    type="number"
                                    id="salary_pension"
                                    name="salary_pension"
                                    className="form-control"
                                    value={formData.salary_pension || ''}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="inheritance">Inheritance</label>
                                <p className="help-text">Whole number.</p>
                                <input
                                    type="number"
                                    id="inheritance"
                                    name="inheritance"
                                    className="form-control"
                                    value={formData.inheritance || ''}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="investment_income">Investment / trading income</label>
                                <p className="help-text">Whole number.</p>
                                <input
                                    type="number"
                                    id="investment_income"
                                    name="investment_income"
                                    className="form-control"
                                    value={formData.investment_income || ''}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="allowance_spouse">Allowance / spouse income</label>
                                <p className="help-text">Whole number.</p>
                                <input
                                    type="number"
                                    id="allowance_spouse"
                                    name="allowance_spouse"
                                    className="form-control"
                                    value={formData.allowance_spouse || ''}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="other_sources">Others</label>
                                <p className="help-text">Whole number.</p>
                                <input
                                    type="number"
                                    id="other_sources"
                                    name="other_sources"
                                    className="form-control"
                                    value={formData.other_sources || ''}
                                    onChange={handleInputChange}
                                    min="0"
                                    max="100"
                                />
                            </div>
                        </div>
                    )}

                    {currentStep === 5 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px 12px', marginBottom: '20px', fontWeight: '700', fontSize: '13px' }}>
                                ANTICIPATED INVESTMENT &amp; FUNDING
                            </div>

                            <div className="form-group">
                                <label htmlFor="anticipated_investment">Anticipated investment with Kristal (USD) <span className="label-required">*</span></label>
                                <p className="help-text">Initial amount plus expected flows over the next 12 months.</p>
                                <input
                                    type="text"
                                    id="anticipated_investment"
                                    name="anticipated_investment"
                                    className={`form-control ${errors.anticipated_investment ? 'error' : ''}`}
                                    value={formData.anticipated_investment || ''}
                                    onChange={handleInputChange}
                                />
                                {errors.anticipated_investment && <span className="error-message">{errors.anticipated_investment}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="funding_method">Funding Method <span className="label-required">*</span></label>
                                <select
                                    id="funding_method"
                                    name="funding_method"
                                    className={`form-control ${errors.funding_method ? 'error' : ''}`}
                                    value={formData.funding_method || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">SELECT...</option>
                                    <option value="cash_remittance">Cash remittance</option>
                                    <option value="asset_transfer">Asset transfer</option>
                                    <option value="both">Both</option>
                                </select>
                                {errors.funding_method && <span className="error-message">{errors.funding_method}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="remitting_bank">Remitting bank &amp; jurisdiction</label>
                                <p className="help-text">Account must be held in the client's own name; third-party remittances are not accepted.</p>
                                <textarea
                                    id="remitting_bank"
                                    name="remitting_bank"
                                    className="form-control"
                                    value={formData.remitting_bank || ''}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    )}

                    {currentStep === 6 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px 12px', marginBottom: '20px', fontWeight: '700', fontSize: '13px' }}>
                                SOURCE OF WEALTH &amp; FUNDS NARRATIVE
                            </div>

                            <div className="form-group">
                                <label htmlFor="wealth_narrative">Summary of the activities that led to the build-up of wealth</label>
                                <p className="help-text">Career history, businesses owned or sold, inheritance, property disposals, investment activity - with approximate periods and amounts.</p>
                                <textarea
                                    id="wealth_narrative"
                                    name="wealth_narrative"
                                    className="form-control"
                                    value={formData.wealth_narrative || ''}
                                    onChange={handleInputChange}
                                    style={{ minHeight: '150px' }}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="corroboration_documents">Key corroborating documents</label>
                                <p className="help-text">e.g. portfolio statement (institution + date), bank statements, inheritance deed, CA certificate, tax returns.</p>
                                <textarea
                                    id="corroboration_documents"
                                    name="corroboration_documents"
                                    className="form-control"
                                    value={formData.corroboration_documents || ''}
                                    onChange={handleInputChange}
                                    style={{ minHeight: '150px' }}
                                />
                            </div>
                        </div>
                    )}

                    {currentStep === 7 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <p className="help-text" style={{ marginBottom: '25px', fontWeight: 'normal' }}>Tick the boxes that apply. Select all that apply for accounts held, one box each for the remaining questions.</p>

                            <div className="form-group">
                                <label>Investment / private banking accounts held</label>
                                <p className="help-text">Select all that apply.</p>
                                <div className="checkbox-group">
                                    {['Multiple Private Banking Accounts', 'One Private Banking Account', 'Online broking / digital investment platform account', 'Only a basic (savings/checking/retail) bank account'].map((option, idx) => (
                                        <div key={idx} className="checkbox-item">
                                            <input
                                                type="checkbox"
                                                id={`accounts_${idx}`}
                                                name="accounts_held"
                                                value={option}
                                                checked={(formData.accounts_held || []).includes(option)}
                                                onChange={(e) => handleMultiSelect('accounts_held', option)}
                                            />
                                            <label htmlFor={`accounts_${idx}`}>{option}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="education_level">Highest level of education <span className="label-required">*</span></label>
                                <p className="help-text">Finance includes CFA, accountancy, markets, financial engineering, MBA.</p>
                                <div className="radio-group">
                                    {[
                                        { value: 'postgrad_finance', label: 'Postgraduate or more in Finance' },
                                        { value: 'grad_finance', label: 'Graduate in Finance' },
                                        { value: 'postgrad_nonfinance', label: 'Postgraduate or more in Non-Finance' },
                                        { value: 'grad_nonfinance', label: 'Graduate in Non-Finance' },
                                        { value: 'undergrad_highschool', label: 'Undergraduate / High School' }
                                    ].map(option => (
                                        <div key={option.value} className="radio-item">
                                            <input
                                                type="radio"
                                                id={`edu_${option.value}`}
                                                name="education_level"
                                                value={option.value}
                                                checked={formData.education_level === option.value}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor={`edu_${option.value}`}>{option.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.education_level && <span className="error-message">{errors.education_level}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="portfolio_loss">Maximum portfolio loss over the past 5 years <span className="label-required">*</span></label>
                                <div className="radio-group">
                                    {[
                                        { value: 'more_20', label: 'Lost more than 20%' },
                                        { value: '10_20', label: 'Lost between 10% to 20%' },
                                        { value: 'less_10', label: 'Lost less than 10%' },
                                        { value: 'no_loss', label: 'No Loss' }
                                    ].map(option => (
                                        <div key={option.value} className="radio-item">
                                            <input
                                                type="radio"
                                                id={`loss_${option.value}`}
                                                name="portfolio_loss"
                                                value={option.value}
                                                checked={formData.portfolio_loss === option.value}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor={`loss_${option.value}`}>{option.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.portfolio_loss && <span className="error-message">{errors.portfolio_loss}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="financial_experience">Years of experience in the financial industry <span className="label-required">*</span></label>
                                <p className="help-text">Advisory / trading / sales / structuring / accountancy / financial markets.</p>
                                <div className="radio-group">
                                    {[
                                        { value: '3plus', label: '3+ years' },
                                        { value: '2years', label: '2 years' },
                                        { value: '1year', label: '1 year' },
                                        { value: 'none', label: 'No experience' }
                                    ].map(option => (
                                        <div key={option.value} className="radio-item">
                                            <input
                                                type="radio"
                                                id={`exp_${option.value}`}
                                                name="financial_experience"
                                                value={option.value}
                                                checked={formData.financial_experience === option.value}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor={`exp_${option.value}`}>{option.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.financial_experience && <span className="error-message">{errors.financial_experience}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="comfortable_downside">Comfortable downside / annual return <span className="label-required">*</span></label>
                                <div className="radio-group">
                                    {[
                                        { value: 'minus15_plus20', label: '-15% loss / +20% gain' },
                                        { value: 'minus12_plus15', label: '-12% loss / +15% gain' },
                                        { value: 'minus9_plus12', label: '-9% loss / +12% gain' },
                                        { value: 'minus6_plus9', label: '-6% loss / +9% gain' },
                                        { value: 'minus4_plus6', label: '-4% loss / +6% gain' }
                                    ].map(option => (
                                        <div key={option.value} className="radio-item">
                                            <input
                                                type="radio"
                                                id={`risk_${option.value}`}
                                                name="comfortable_downside"
                                                value={option.value}
                                                checked={formData.comfortable_downside === option.value}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor={`risk_${option.value}`}>{option.label}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.comfortable_downside && <span className="error-message">{errors.comfortable_downside}</span>}
                            </div>

                            {['Transactions (3 yrs) - mutual funds / stocks / ETFs / bonds', 'Transactions (3 yrs) - futures / options / CFDs', 'Transactions (3 yrs) - hedge funds / arbitrage funds', 'Transactions (3 yrs) - structured notes / FCNs / RCNs', 'Transactions (3 yrs) - crypto (currency / funds / ETFs / options)'].map((label, idx) => (
                                <div key={idx} className="form-group">
                                    <label>{label}</label>
                                    <div className="radio-group">
                                        {['Yes', 'No'].map(option => (
                                            <div key={option} className="radio-item">
                                                <input
                                                    type="radio"
                                                    id={`trans_${idx}_${option}`}
                                                    name={`transaction_${idx}`}
                                                    value={option}
                                                    checked={formData[`transaction_${idx}`] === option}
                                                    onChange={handleInputChange}
                                                />
                                                <label htmlFor={`trans_${idx}_${option}`}>{option}</label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <div className="form-group">
                                <label htmlFor="pep_status">Politically Exposed Person (PEP)? <span className="label-required">*</span></label>
                                <p className="help-text">If Yes, complete the PEP appendix.</p>
                                <div className="radio-group">
                                    {['Yes', 'No'].map(option => (
                                        <div key={option} className="radio-item">
                                            <input
                                                type="radio"
                                                id={`pep_${option}`}
                                                name="pep_status"
                                                value={option}
                                                checked={formData.pep_status === option}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor={`pep_${option}`}>{option}</label>
                                        </div>
                                    ))}
                                </div>
                                {errors.pep_status && <span className="error-message">{errors.pep_status}</span>}
                            </div>
                        </div>
                    )}

                    {currentStep === 8 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px 12px', marginBottom: '20px', fontWeight: '700', fontSize: '13px' }}>
                                PEP APPENDIX - COMPLETE ONLY WHERE PEP = YES
                            </div>

                            {formData.pep_status === 'Yes' ? (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="pep_position_government">Position(s) held in government</label>
                                        <p className="help-text">By the individual or family members in the last 10 years.</p>
                                        <textarea
                                            id="pep_position_government"
                                            name="pep_position_government"
                                            className="form-control"
                                            value={formData.pep_position_government || ''}
                                            onChange={handleInputChange}
                                            style={{ minHeight: '100px' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="pep_political_party">Affiliations / positions in a political party</label>
                                        <p className="help-text">By the individual or family members in the last 10 years.</p>
                                        <textarea
                                            id="pep_political_party"
                                            name="pep_political_party"
                                            className="form-control"
                                            value={formData.pep_political_party || ''}
                                            onChange={handleInputChange}
                                            style={{ minHeight: '100px' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="pep_regulatory_body">Positions in regulatory / governing bodies established or sponsored by government</label>
                                        <textarea
                                            id="pep_regulatory_body"
                                            name="pep_regulatory_body"
                                            className="form-control"
                                            value={formData.pep_regulatory_body || ''}
                                            onChange={handleInputChange}
                                            style={{ minHeight: '100px' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="pep_legal_matters">Past / outstanding legal matters</label>
                                        <p className="help-text">Brought by or against the individual or family members in the last 10 years.</p>
                                        <textarea
                                            id="pep_legal_matters"
                                            name="pep_legal_matters"
                                            className="form-control"
                                            value={formData.pep_legal_matters || ''}
                                            onChange={handleInputChange}
                                            style={{ minHeight: '100px' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="pep_aum_kristal">Expected assets under management / advisory with Kristal (USD)</label>
                                        <input
                                            type="text"
                                            id="pep_aum_kristal"
                                            name="pep_aum_kristal"
                                            className="form-control"
                                            value={formData.pep_aum_kristal || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </>
                            ) : (
                                <p style={{ padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'center', color: '#666' }}>
                                    This section is only completed when PEP status is "Yes"
                                </p>
                            )}
                        </div>
                    )}

                    {currentStep === 9 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px 12px', marginBottom: '20px', fontWeight: '700', fontSize: '13px' }}>
                                SUPPORTING DOCUMENTS REGISTER
                            </div>

                            <p className="help-text" style={{ marginBottom: '20px' }}>Every document must be in original (not password-protected) and the most recently dated version available.</p>

                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #333' }}>
                                            <th style={{ textAlign: 'left', padding: '10px', fontWeight: '700', fontSize: '14px' }}>DOCUMENT</th>
                                            <th style={{ textAlign: 'left', padding: '10px', fontWeight: '700', fontSize: '14px' }}>STATUS</th>
                                            <th style={{ textAlign: 'left', padding: '10px', fontWeight: '700', fontSize: '14px' }}>UPLOAD FILE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { name: 'ID proof (passport)', field: 'doc_id_proof_status', fileField: 'doc_id_proof_file' },
                                            { name: 'Residency proof (utility bill / bank statement, <= 3 months)', field: 'doc_residency_status', fileField: 'doc_residency_file' },
                                            { name: 'Official tax ID copy (e.g. PAN) / national ID', field: 'doc_tax_id_status', fileField: 'doc_tax_id_file' },
                                            { name: 'Accredited Investor evidence (portfolio / bank statements, <= 90 days)', field: 'doc_ai_evidence_status', fileField: 'doc_ai_evidence_file' },
                                            { name: 'Source of Funds - 100% of proposed investment', field: 'doc_source_funds_status', fileField: 'doc_source_funds_file' },
                                            { name: 'Source of Wealth - >= 50% of declared net worth', field: 'doc_source_wealth_status', fileField: 'doc_source_wealth_file' },
                                            { name: 'Other supporting documents', field: 'doc_other_status', fileField: 'doc_other_file' }
                                        ].map((doc, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                                                <td style={{ padding: '10px', fontSize: '14px' }}>{doc.name}</td>
                                                <td style={{ padding: '10px' }}>
                                                    <select
                                                        name={doc.field}
                                                        className="form-control"
                                                        value={formData[doc.field] || ''}
                                                        onChange={handleInputChange}
                                                        style={{ maxWidth: '200px' }}
                                                    >
                                                        <option value="">SELECT STATUS</option>
                                                        <option value="Attached">Attached</option>
                                                        <option value="Pending">Pending</option>
                                                        <option value="Not applicable">Not applicable</option>
                                                    </select>
                                                </td>
                                                <td style={{ padding: '10px' }}>
                                                    <input
                                                        type="file"
                                                        id={`file_${idx}`}
                                                        style={{ display: 'none' }}
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        onChange={(e) => handleFileChange(e, doc.fileField)}
                                                    />
                                                    <label
                                                        htmlFor={`file_${idx}`}
                                                        style={{
                                                            display: 'inline-block',
                                                            padding: '8px 16px',
                                                            backgroundColor: '#667eea',
                                                            color: '#fff',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: '500',
                                                            border: 'none',
                                                            transition: 'background 0.3s'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.backgroundColor = '#764ba2'}
                                                        onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
                                                    >
                                                        Choose File
                                                    </label>
                                                    {formData[doc.fileField] && (
                                                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#28a745' }}>
                                                            ✓ {formData[doc.fileField].name}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {currentStep === 10 && (
                        <div className="form-section">
                            <div className="section-header">
                                <div className="section-icon">{currentStepData.icon}</div>
                                <h2 className="section-title">{currentStepData.title}</h2>
                            </div>

                            <div className="form-group">
                                <label htmlFor="id_proof">ID Proof (Passport) <span className="label-required">*</span></label>
                                <div
                                    className={`file-upload-wrapper ${errors.id_proof ? 'error' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, 'id_proof')}
                                >
                                    <label className="file-upload-label" htmlFor="id_proof_upload">
                                        <input
                                            type="file"
                                            id="id_proof_upload"
                                            name="id_proof"
                                            className="file-upload-input"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="file-upload-text">Original passport copy - valid and unexpired</div>
                                        <div className="file-upload-button">Choose File</div>
                                    </label>
                                    {formData.id_proof && <div className="file-display">Selected: {formData.id_proof.name}</div>}
                                </div>
                                {errors.id_proof && <span className="error-message">{errors.id_proof}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="address_proof">Residency Proof <span className="label-required">*</span></label>
                                <div
                                    className={`file-upload-wrapper ${errors.address_proof ? 'error' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, 'address_proof')}
                                >
                                    <label className="file-upload-label" htmlFor="address_proof_upload">
                                        <input
                                            type="file"
                                            id="address_proof_upload"
                                            name="address_proof"
                                            className="file-upload-input"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="file-upload-text">Utility bill or bank statement (within last 3 months)</div>
                                        <div className="file-upload-button">Choose File</div>
                                    </label>
                                    {formData.address_proof && <div className="file-display">Selected: {formData.address_proof.name}</div>}
                                </div>
                                {errors.address_proof && <span className="error-message">{errors.address_proof}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="tax_id_copy">Official Tax ID Copy <span className="label-required">*</span></label>
                                <div
                                    className={`file-upload-wrapper ${errors.tax_id_copy ? 'error' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, 'tax_id_copy')}
                                >
                                    <label className="file-upload-label" htmlFor="tax_id_copy_upload">
                                        <input
                                            type="file"
                                            id="tax_id_copy_upload"
                                            name="tax_id_copy"
                                            className="file-upload-input"
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                        />
                                        <div className="file-upload-text">e.g. PAN card, national ID copy</div>
                                        <div className="file-upload-button">Choose File</div>
                                    </label>
                                    {formData.tax_id_copy && <div className="file-display">Selected: {formData.tax_id_copy.name}</div>}
                                </div>
                                {errors.tax_id_copy && <span className="error-message">{errors.tax_id_copy}</span>}
                            </div>

                            <div className="form-group">
                                <label className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        name="declaration"
                                        checked={formData.declaration || false}
                                        onChange={handleInputChange}
                                    />
                                    <label style={{ margin: '0', marginLeft: '8px', fontWeight: '500' }}>
                                        I declare that all information provided in this form is accurate and complete
                                    </label>
                                </label>
                                {errors.declaration && <span className="error-message">{errors.declaration}</span>}
                            </div>
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handlePrevious}
                        disabled={currentStep === 0}
                    >
                        ← Previous
                    </button>

                    {currentStep < totalSteps - 1 ? (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleNext}
                            disabled={currentStep === 3 && isUSPerson}
                        >
                            Next →
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleReset}
                                disabled={isSubmitting}
                            >
                                Clear Form
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="loading-spinner"></span> Submitting...
                                    </>
                                ) : (
                                    'Submit Application'
                                )}
                            </button>
                        </>
                    )}
                </div>
            </form>
        </div>
    );
}
